import { t, Trans } from '@lingui/macro'
import { Alert, Box, Chip, Divider, FormControlLabel, Paper, Radio, RadioGroup, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'

import { IdentityStatusBadge } from '@/pages/patient/ui/IdentityStatusBadge'
import { isProvisionalIdentityValue } from '@/features/patient/patient-profile-form/lib/identity-naming'

import type { ExternalIdentityPatientData } from '../lib/external-identity'
import type { UpdateBlocker } from '../lib/identity-matching'
import type { ReconciliationCandidate } from '../model/useExternalIdentityReconciliation'

export const CREATE_INSTEAD = '__create_instead__'

type Props = {
  received: ExternalIdentityPatientData
  emitter: string
  candidates: ReconciliationCandidate[]
  selectedId: string
  onSelect: (id: string) => void
  blocker: UpdateBlocker | null
  refusal: 'KO' | 'ERROR' | null
  skippedReason: 'ALREADY_VERIFIED' | 'COLLISION' | null
  disabled: boolean
}

function formatTraits(data: {
  birthSurname?: string | null
  birthFirstName?: string | null
  birthday?: Date | string | null
  gender?: string | null
  birthPlaceCode?: string | null
}) {
  const displayField = (value?: string | null) => (isProvisionalIdentityValue(value) ? t`Unknown` : value)
  const birthday = data.birthday && dayjs(data.birthday).isValid() ? dayjs(data.birthday).format('DD/MM/YYYY') : '—'
  return `${displayField(data.birthSurname) || '—'} ${displayField(data.birthFirstName) || ''} · ${birthday} · ${data.gender || '—'} · ${
    displayField(data.birthPlaceCode) || '—'
  }`
}

function BlockerAlert({ blocker }: { blocker: UpdateBlocker }) {
  const message = {
    DIFFERENT_INS: <Trans>This identity already holds a different INS matricule. Reconciliation must be handled by the identity vigilance officer.</Trans>,
    INS_COLLISION: <Trans>The received INS matricule is already assigned to another identity in the repository.</Trans>,
    INS_CALL_BLOCKED: <Trans>The INSi call is not possible for an identity flagged as doubtful or fictitious.</Trans>,
    CONNECTOR_UNAVAILABLE: <Trans>INSi connector unavailable. Check the DMP Connect connection.</Trans>,
  }[blocker]
  return <Alert severity="warning">{message}</Alert>
}

export function ExternalIdentityReconciliationStep({ received, emitter, candidates, selectedId, onSelect, blocker, refusal, skippedReason, disabled }: Props) {
  return (
    <Stack spacing={2.25} sx={{ mt: 1 }}>
      {skippedReason === 'ALREADY_VERIFIED' && (
        <Alert severity="info">
          <Trans>
            No INSi verification was launched: this identity already holds the received INS matricule with a qualified or recovered status. There is nothing to
            update.
          </Trans>
        </Alert>
      )}
      {skippedReason === 'COLLISION' && (
        <Alert severity="warning">
          <Trans>
            No INSi verification was launched: the received INS matricule is already held by another identity of the repository with a qualified or recovered
            status, whose traits differ. This reconciliation must be handled by the identity vigilance officer.
          </Trans>
        </Alert>
      )}
      {refusal === 'KO' && (
        <Alert severity="error">
          <Trans>
            INSi returned KO: the identity could not be validated. The existing identity has NOT been updated — its INS matricule, its OID and its status are
            unchanged.
          </Trans>
        </Alert>
      )}
      {refusal === 'ERROR' && (
        <Alert severity="warning">
          <Trans>The INSi verification failed. The existing identity has not been updated — you can try again.</Trans>
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          <Trans>Identity transmitted by the emitter</Trans>
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {formatTraits(received)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block', mt: 0.5 }}>
          {received.insNIR} · {received.insOID}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          <Trans>Emitter: {emitter}</Trans>
        </Typography>
      </Paper>

      {skippedReason !== 'COLLISION' && (
        <>
          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              <Trans>Matching identities in your repository</Trans>
            </Typography>
            <RadioGroup value={selectedId} onChange={(event) => onSelect(event.target.value)}>
              {candidates.map(({ patient, kind, divergentTraits }) => (
                <FormControlLabel
                  key={patient._id}
                  value={patient._id}
                  disabled={disabled || kind !== 'EXACT'}
                  control={<Radio />}
                  sx={{ alignItems: 'flex-start', mb: 1, opacity: kind === 'EXACT' ? 1 : 0.6 }}
                  label={
                    <Box sx={{ pt: 0.75 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatTraits(patient.userInfo ?? {})}
                        </Typography>
                        {patient.identityStatus && <IdentityStatusBadge status={patient.identityStatus} />}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
                        {patient.insNIR || <Trans>No INS matricule</Trans>}
                      </Typography>
                      {kind !== 'EXACT' && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="warning"
                          sx={{ mt: 0.5 }}
                          label={<Trans>Divergent traits ({divergentTraits.join(', ')}) — to be reconciled by the identity vigilance officer</Trans>}
                        />
                      )}
                    </Box>
                  }
                />
              ))}
              <FormControlLabel
                value={CREATE_INSTEAD}
                disabled={disabled}
                control={<Radio />}
                label={
                  <Typography variant="body2">
                    <Trans>None of these identities — create a new identity</Trans>
                  </Typography>
                }
              />
            </RadioGroup>
          </Box>
        </>
      )}

      {blocker && <BlockerAlert blocker={blocker} />}
    </Stack>
  )
}
